import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import type { RootState } from '../store';
import { ActivityProp, IndividualFormData, IndividualSlice } from '../../types/interface';
import dayjs from 'dayjs';

interface ActivitySlice {
  status: string;
  error: string | null;
  myActivity: ActivityProp[];
}

const initialState: ActivitySlice = {
  status: 'idle',
  error: null,
  myActivity: [],
};

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    setActivityList: (state: ActivitySlice, action: PayloadAction<{ data: ActivityProp[] }>) => {
      console.warn('Setting list! ', action.payload);
      const sortedList = action.payload.data.sort(
        (a: ActivityProp, b: ActivityProp) => dayjs(b.timestamp).unix() - dayjs(a.timestamp).unix()
      );
      state.myActivity = [...sortedList];
    },
  },
  extraReducers: (builder) => {
    // refer to: https://redux-toolkit.js.org/api/createSlice for more information
    // the builder.addCase() pattern allows us to handle response data within this slice and assign it to our global store. Below we build three cases for one http request called
    // thunkFetchActivity. We update the statusu based accordingly based off of props of the asyncThunk (pending, fulfilled and rejected).
    // Realistically the displayBuffer hook should not exist and we should really be tapping into this section of Redux for UI transitions/access.
    builder
      .addCase(thunkFetchActivity.pending, (state, action) => {
        state.status = 'loading';
      })
      .addCase(thunkFetchActivity.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.myActivity = [...action.payload.data];
        console.warn('THUNK ACTIVITY: ', action.payload.data);
        return;
      })
      .addCase(thunkFetchActivity.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.error.message!;
      });
  },
});

//Create AsynThunk are just wrappers for axios HTTP requests, the .then()  is consumed within the React Components calling the specific AsyncThunk.
// in the example below, we fetch b2c/activity/create from the backend, and then get a response which is access on the client side by the .unwrap() function see JobHeader.tsx.

export const postActivityReport = createAsyncThunk(
  'activity/postActivity',
  async (params: {
    activityObjArr: { field: string; value: string }[];
    info: {
      orgId: string;
      provId: string;
      indId: string;
      tag: string;
      source: string;
    };
    token: string;
  }) => {
    return axios
      .post(
        `${process.env.REACT_APP_SERVER_URL}/api/b2c/activity/create`,
        { rawList: params.activityObjArr!, info: { ...params.info } },
        {
          headers: {
            Authorization: `Bearer ${params.token}`,
          },
        }
      )
      .then((response) => {
        console.info('Reducer Activity Record POST:', response);
        return response.data;
      })
      .catch((err) => {
        return err.message;
      });
  }
);

export const addLogReport = createAsyncThunk(
  'activity/logUser',
  async (params: {
    activityObjArr: { field: string; value: string }[];
    info: {
      orgId: string;
      provId: string;
      indId: string;
      tag: string;
    };
    token: string;
  }) => {
    return axios
      .post(
        `${process.env.REACT_APP_SERVER_URL}/api/b2c/activity/log`,
        { rawList: params.activityObjArr!, info: { ...params.info } },
        {
          headers: {
            Authorization: `Bearer ${params.token}`,
          },
        }
      )
      .then((response) => {
        console.info('Reducer logged user!:', response);
        return response.data;
      })
      .catch((err) => {
        return err.message;
      });
  }
);

export const thunkFetchActivity = createAsyncThunk(
  'activity/get',
  async (params: { indId: string; token: string }) => {
    return axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/b2c/activity/${params.indId}`, {
        headers: {
          Authorization: `Bearer ${params.token}`,
        },
      })
      .then((response) => {
        console.info('Reducer Activity FETCH', response);
        return response.data;
      })
      .catch((err) => {
        return err.message;
      });
  }
);

export const thunkFetchCustomActivity = createAsyncThunk(
  'activity/get',
  async (params: { indId: string; start: string; end: string; token: string }) => {
    return axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/b2c/activity/custom/${params.indId}`, {
        headers: {
          Authorization: `Bearer ${params.token}`,
        },
        params: {
          start: params.start,
          end: params.end,
        },
      })
      .then((response) => {
        console.info('Reducer Activity FETCH', response);
        return response.data;
      })
      .catch((err) => {
        return err.message;
      });
  }
);

export const getAllActivity = (state: RootState) => {
  return state.activity.myActivity;
};
export const { setActivityList } = activitySlice.actions;

export default activitySlice.reducer;
